import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import NFLMatchupCard from './NFLMatchupCard';
import NFLSabotageCard from './NFLSabotageCard';
import NFLActivityFeed from './NFLActivityFeed';
import NFLStandingsTable from './NFLStandingsTable';
import NFLLeagueLobby from './NFLLeagueLobby';
import NFLCommissionerChecklist from './NFLCommissionerChecklist';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlusCircle, UserPlus, Users, Bomb, Trophy, DollarSign, Search } from 'lucide-react';
import OnboardingBanner from '@/components/shared/OnboardingBanner';

interface NFLHomePageProps {
  onNavigate: (tab: string) => void;
}

export default function NFLHomePage({ onNavigate }: NFLHomePageProps) {
  const { currentLeague, currentWeek, teams, isLoading } = useNFLLeague();
  const navigate = useNavigate();

  // Season sabotage count badge. Keyed so it refreshes on tab return (Home remounts on tab
  // switch → refetch); a count query never gates the view, so no spinner concern.
  const { data: sabotageCount = 0 } = useQuery({
    queryKey: ['nfl-sabotage-count', currentLeague?.id],
    enabled: !!currentLeague,
    staleTime: 30_000,
    queryFn: async (): Promise<number> => {
      const { count } = await supabase
        .from('nfl_sabotage_events' as any)
        .select('id', { count: 'exact', head: true })
        .eq('league_id', currentLeague!.id);
      return count || 0;
    },
  });

  /**
   * SAB-1000: the league's REAL prize pool.
   *
   * ── WHY THIS IS A QUERY AND NOT currentLeague.prize_pool ────────────────
   *
   * Once a schedule is frozen, `league_prize_schedule.frozen_pool` is the number that
   * actually pays out; `nfl_leagues.prize_pool` is merely the value it was snapshotted
   * from and can drift afterwards. `snapshot_prize_schedule` reads prize_pool into
   * frozen_pool at freeze time, so before a freeze they agree and prize_pool is the only
   * source — after one, only frozen_pool is authoritative. Showing a number that differs
   * from what pays is the same class of error as the hardcoded $1,500 this replaces.
   *
   * Returns undefined while loading, which suppresses the tile rather than flashing a
   * wrong figure. `null` means "asked, and there is no frozen schedule".
   */
  const { data: frozenPool, isPending: poolPending } = useQuery({
    queryKey: ['nfl-frozen-pool', currentLeague?.id],
    enabled: !!currentLeague,
    staleTime: 30_000,
    queryFn: async (): Promise<number | null> => {
      const { data } = await supabase
        .from('league_prize_schedule' as any)
        .select('frozen_pool')
        .eq('league_id', currentLeague!.id)
        .not('frozen_pool', 'is', null)
        .limit(1)
        .maybeSingle();
      const v = (data as { frozen_pool?: number | string | null } | null)?.frozen_pool;
      return v === null || v === undefined ? null : Number(v);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentLeague) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
          <span className="text-3xl">🏈</span>
        </div>
        <h1 className="text-2xl font-display font-bold text-foreground mb-2">
          Sabotage Football
        </h1>
        <p className="text-muted-foreground max-w-md mb-8">
          Join a league or create your own to start sabotaging your friends with the worst football players.
        </p>
        {/* SAB-425: three entry points — Create / Join-with-code / Browse */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
            onClick={() => onNavigate('create')}
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Create League
          </Button>
          <Button
            variant="outline"
            className="border-border text-foreground hover:bg-muted"
            onClick={() => onNavigate('join')}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Join with Code
          </Button>
          <Button
            variant="outline"
            className="border-border text-foreground hover:bg-muted"
            onClick={() => navigate('/nfl/leagues/browse')}
          >
            <Search className="h-4 w-4 mr-2" />
            Browse Public Leagues
          </Button>
        </div>
      </div>
    );
  }

  // Show lobby when league is in pre-draft
  if (currentLeague.status === 'pre_draft') {
    return (
      <div className="space-y-4">
        <div className="text-center space-y-1">
          <h1 className="text-xl font-display font-bold text-foreground">
            {currentLeague.name}
          </h1>
          <p className="text-xs text-muted-foreground">Waiting for teams to join · Pre-Draft Lobby</p>
        </div>
        <NFLCommissionerChecklist onNavigate={onNavigate} />
        <NFLLeagueLobby onStartDraft={() => onNavigate('draft')} />
      </div>
    );
  }

  /**
   * SAB-1000: which money tiles this league shows, and what they say.
   *
   * ── WHAT WAS THERE ─────────────────────────────────────────────────────
   *
   * `value="$1,500"` and `value="$360"`, both string literals, inherited from the MLB
   * dashboard. League of Trash has entry_fee 0 and frozen_pool 0.00, so the banner was
   * announcing a $1,500 prize pool for a free league. On a real-money platform that is not
   * a cosmetic bug; it misstates money to the people playing for it.
   *
   * ── SABOTAGE REV IS DELETED, NOT FIXED ─────────────────────────────────
   *
   * There is no NFL source for it: `nfl_leagues` has no sabotage_revenue column (MLB's
   * `leagues` table does) and `nfl_sabotage_events` carries no cost or price, so the
   * revenue cannot be summed either. Any value would have to be invented, which is the bug.
   * MLB reached the same conclusion independently in MLB-028 and dropped the tile from the
   * user-facing dashboard. Removed here rather than bound to a placeholder.
   *
   * ── WHEN A MONEY TILE APPEARS ──────────────────────────────────────────
   *
   * Each tile is shown only when it has something true to say, so a free league renders
   * TEAMS and SABOTAGES alone -- never a $0 buy-in or a $0 pool.
   *
   * The ticket phrased the rule as "entry_fee > 0 WITH a prize schedule". I have split the
   * conjunction, because on prod that rule shows nothing anywhere: 4 leagues charge an
   * entry fee and ZERO of them have a frozen schedule, including the one active paid league
   * (Rehearsal E2E A, $10 entry, 12 teams). Hiding a $10 buy-in from a league that charges
   * $10 is the same failure as inventing $1,500 -- the banner disagreeing with the money.
   * So the buy-in stands on entry_fee alone, and the pool appears only when a pool exists.
   */
  const entryFeeRaw = Number(currentLeague?.entry_fee ?? 0);
  const entryFee = entryFeeRaw > 0 ? entryFeeRaw : null;

  // frozen_pool wins when a schedule is frozen; prize_pool is the pre-freeze source. While
  // the query is in flight the tile is withheld, so a stale or wrong figure never paints.
  const effectivePool = poolPending
    ? null
    : (frozenPool ?? Number(currentLeague?.prize_pool ?? 0));
  const prizePool = effectivePool !== null && effectivePool > 0 ? effectivePool : null;

  // 2, 3 or 4 tiles. Enumerated rather than computed into a template string because
  // Tailwind scans source text for class names -- `md:grid-cols-${n}` is never emitted and
  // the row silently collapses to one column at desktop width.
  const tileCount = 2 + (entryFee !== null ? 1 : 0) + (prizePool !== null ? 1 : 0);
  const gridCols = tileCount === 4 ? 'md:grid-cols-4'
    : tileCount === 3 ? 'md:grid-cols-3'
    : 'md:grid-cols-2';

  return (
    <div className="space-y-6">
      <OnboardingBanner />
      {/* Welcome Hero */}
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-muted-foreground">WEEK {currentWeek}</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold font-display text-foreground">
            Welcome to <span className="text-primary">Sabotage</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Where fumbles are touchdowns and chaos reigns supreme.
          </p>

          {/* Stats Row — SAB-1000. Tile SET is per-league; see moneyTiles above. */}
          <div className={`grid grid-cols-2 gap-3 mt-5 ${gridCols}`}>
            <StatPill
              icon={<Users className="w-4 h-4 text-muted-foreground" />}
              value={teams.length}
              label="Teams"
              onClick={() => onNavigate('standings')}
            />
            <StatPill
              icon={<Bomb className="w-4 h-4 text-muted-foreground" />}
              value={sabotageCount}
              label="Sabotages"
            />
            {entryFee !== null && (
              <StatPill
                icon={<DollarSign className="w-4 h-4 text-muted-foreground" />}
                value={formatMoney(entryFee)}
                label="Buy-In"
              />
            )}
            {prizePool !== null && (
              <StatPill
                icon={<Trophy className="w-4 h-4 text-muted-foreground" />}
                value={formatMoney(prizePool)}
                label="Prize Pool"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Your Matchup */}
      <NFLMatchupCard onNavigate={onNavigate} currentWeek={currentWeek} />

      {/* Two-column layout on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left column — transactions + sabotage feed */}
        <div className="space-y-4">
          <NFLActivityFeed />
          <NFLSabotageCard />
        </div>

        {/* Right column — standings */}
        <div className="space-y-4">
          <NFLStandingsTable onNavigate={onNavigate} />
        </div>
      </div>
    </div>
  );
}

/**
 * SAB-1000: whole dollars, grouped. Cents are dropped only when the amount is whole, so a
 * $12.50 buy-in still reads $12.50 rather than being silently rounded to $13 -- rounding a
 * figure someone is charged is the same class of misstatement this ticket removes.
 */
function formatMoney(n: number): string {
  const whole = Number.isInteger(n);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
}

function StatPill({ icon, value, label, onClick }: {
  icon: React.ReactNode;
  value: number | string;
  label: string;
  onClick?: () => void;
}) {
  return (
    <div
      className={`flex items-center gap-2.5 p-3 rounded-lg bg-muted/40 border border-border ${onClick ? 'cursor-pointer hover:border-primary/30 transition-colors' : ''}`}
      onClick={onClick}
    >
      {icon}
      <div>
        <p className="text-lg font-bold font-display text-foreground">{value}</p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
      </div>
    </div>
  );
}
